import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from 'react';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  Box,
  Typography,
} from '@mui/material';
import ecommerceService from '../../services/ecommerceService';

/**
 * StorefrontConfigContext
 *
 * Provides the consolidated, tenant-configurable storefront configuration
 * (branding, banners, homepage sections, featured categories, product
 * features, navigation) plus a derived MUI theme and currency helpers to the
 * whole storefront. Fetched once from `/api/ecommerce/storefront-config/`.
 */
const StorefrontConfigContext = createContext(null);

export const useStorefrontConfig = () => {
  const ctx = useContext(StorefrontConfigContext);
  if (!ctx) {
    // Safe fallback so components never crash outside the provider.
    return {
      settings: {},
      banners: [],
      sections: [],
      featuredCategories: [],
      productFeatures: [],
      navigation: [],
      loading: false,
      error: null,
      currencySymbol: '₹',
      formatPrice: (v) => `₹${Number(v || 0).toFixed(2)}`,
      reload: () => {},
    };
  }
  return ctx;
};

const FALLBACK_COLORS = {
  primary: '#111111',
  secondary: '#6c757d',
  accent: '#d32f2f',
};

const buildTheme = (settings) =>
  createTheme({
    palette: {
      primary: { main: settings?.primary_color || FALLBACK_COLORS.primary },
      secondary: { main: settings?.secondary_color || FALLBACK_COLORS.secondary },
      error: { main: settings?.accent_color || FALLBACK_COLORS.accent },
      background: { default: '#ffffff', paper: '#ffffff' },
    },
    typography: {
      fontFamily:
        '"Inter","Helvetica Neue",Helvetica,Arial,"Segoe UI",sans-serif',
      h1: { fontWeight: 800, letterSpacing: '-0.02em' },
      h2: { fontWeight: 800, letterSpacing: '-0.02em' },
      h3: { fontWeight: 700 },
      h4: { fontWeight: 700 },
      h5: { fontWeight: 600 },
      h6: { fontWeight: 600 },
      button: { fontWeight: 600, textTransform: 'none' },
    },
    shape: { borderRadius: 8 },
    components: {
      MuiButton: {
        styleOverrides: {
          root: { borderRadius: 999, paddingInline: 20 },
          containedPrimary: { boxShadow: 'none' },
        },
      },
      MuiAppBar: { defaultProps: { color: 'inherit' } },
    },
  });

/** Apply favicon / document title / meta from settings. */
const applyDocumentHead = (settings) => {
  if (!settings) return;
  const title = settings.meta_title || settings.store_name;
  if (title) document.title = title;

  if (settings.meta_description) {
    let tag = document.querySelector('meta[name="description"]');
    if (!tag) {
      tag = document.createElement('meta');
      tag.setAttribute('name', 'description');
      document.head.appendChild(tag);
    }
    tag.setAttribute('content', settings.meta_description);
  }

  if (settings.favicon) {
    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.href = settings.favicon;
  }
};

const MaintenanceScreen = ({ settings }) => (
  <Box
    sx={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
      p: 4,
      gap: 2,
    }}
  >
    {settings?.logo && (
      <Box component="img" src={settings.logo} alt={settings.store_name} sx={{ height: 56 }} />
    )}
    <Typography variant="h4">We'll be back soon</Typography>
    <Typography color="text.secondary" sx={{ maxWidth: 480 }}>
      {settings?.maintenance_message ||
        'Our store is temporarily down for maintenance. Please check back shortly.'}
    </Typography>
  </Box>
);

const StorefrontConfigProvider = ({ children }) => {
  const [config, setConfig] = useState({
    settings: {},
    banners: [],
    sections: [],
    featuredCategories: [],
    productFeatures: [],
    navigation: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await ecommerceService.getStorefrontConfig();
      setConfig({
        settings: data.settings || {},
        banners: data.banners || [],
        sections: data.sections || [],
        featuredCategories: data.featured_categories || [],
        productFeatures: data.product_features || [],
        navigation: data.navigation || [],
      });
      setError(null);
      applyDocumentHead(data.settings);
    } catch (err) {
      // Non-fatal: storefront renders with sensible fallbacks.
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const theme = useMemo(() => buildTheme(config.settings), [config.settings]);

  const currencySymbol = config.settings?.currency_symbol || '₹';
  const currencyCode = config.settings?.currency_code || 'INR';

  const formatPrice = useCallback(
    (value) => {
      const num = Number(value);
      if (Number.isNaN(num)) return value;
      return `${currencySymbol}${num.toLocaleString('en-IN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    },
    [currencySymbol]
  );

  const value = useMemo(
    () => ({
      ...config,
      loading,
      error,
      currencySymbol,
      currencyCode,
      formatPrice,
      reload: load,
    }),
    [config, loading, error, currencySymbol, currencyCode, formatPrice, load]
  );

  const inMaintenance = config.settings?.maintenance_mode;

  return (
    <StorefrontConfigContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {inMaintenance ? <MaintenanceScreen settings={config.settings} /> : children}
      </ThemeProvider>
    </StorefrontConfigContext.Provider>
  );
};

export default StorefrontConfigProvider;
